import { DataSignal } from 's-js';
import { SArray as ArraySignal } from 's-array';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';
import Currency from 'lib/Currency';
import objectCreate from 'lib/objectCreate';

import { ApprovedCharge } from 'data/models/ApprovedCharge';
import { BillItem } from 'data/models/BillItem';
import { BillingSystem } from 'data/models/BillingSystem';
import { TotalPriceForCharges } from 'data/models/Charge';

import { GridColumn, RowSorter } from 'portcall/gridcomponents/model';

// TH 05/03/2017 -- For now, rounding all prices to nearest cent.
// This aligns w/ what QuickBooks requires.
// In the future, I could see needing to make this configurable.
export const PRICE_PRECISION = 2;

const emDash = '\u2014';

const
    InvoiceNumber : GridColumn<ExportGroup> = {
        Title: 'Invoice Number',
        DisplayValue: g => g.InvoiceNumber(),
        SortValue: g => g.InvoiceNumber(),
        ClassName: 'invoice-number'
    },
    VesselName : GridColumn<ExportGroup> = {
        Title: 'Vessel',
        DisplayValue: g => g.VesselName() || emDash,
        SortValue: g => g.VesselName() || '',
        ClassName: 'vessel'
    },
    QBCustomerId : GridColumn<ExportGroup> = {
        Title: 'Customer ID',
        DisplayValue: g => g.QBCustomerId(),
        SortValue: g => g.QBCustomerId(),
        ClassName: 'customer'
    },
    InvoiceDate : GridColumn<ExportGroup> = {
        Title: 'Invoice Date',
        DisplayValue: g => g.InvoiceDate() ? tzmoment(g.InvoiceDate()!).format('M/D/YY') : `${emDash}/${emDash}/${emDash}`,
        SortValue: g => g.InvoiceDate() || new TZDate(0),
        ClassName: 'invoice-date'
    },
    ExportDate : GridColumn<ExportGroup> = {
        Title: 'Export Date',
        DisplayValue: g => g.ExportDate() ? tzmoment(g.ExportDate()!).format('M/D/YY') : `${emDash}/${emDash}/${emDash}`,
        SortValue: g => g.ExportDate() || new TZDate(0),
        ClassName: 'export-date'
    },
    InvoiceTotal : GridColumn<ExportGroup> = {
        Title: 'Invoice Total',
        DisplayValue: g => g.InvoiceTotal.toString({ places: PRICE_PRECISION }),
        // TH 05/05/2017 -- Our expectation is that all charges generated for a BillingSystem
        // will be in a uniform currency. So it should be safe to compare on Num alone.
        SortValue: g => g.InvoiceTotal.Num(),
        ClassName: 'invoice-total'
    };

const DefaultSortColumn = InvoiceDate;

export const Columns = [
    InvoiceNumber,
    VesselName,
    QBCustomerId,
    InvoiceDate,
    ExportDate,
    InvoiceTotal
];

export interface ExportGroupOptions {
    InvoiceNumber : () => string;
    VesselName : () => string | null;
    QBCustomerId : () => string;
    InvoiceDate : () => TZDate | null;
    ExportDate : () => TZDate | null;
    UniqueKey : () => string;
    Charges : ArraySignal<ApprovedCharge | BillItem>;
    SelectedForExport : DataSignal<boolean>;
    Expanded : DataSignal<boolean>;
}

export const FilterData = (g : ExportGroupOptions) =>
    g.InvoiceNumber()      + ' ' +
    (g.VesselName() || '') + ' ' +
    g.QBCustomerId();

export interface ExportGroupListParams {
    BillingSystem : BillingSystem;
    ExportGroups : ArraySignal<ExportGroupOptions>;
    ShowGroupSelectors : () => boolean;
}

export function ExportGroupList (
    { BillingSystem, ExportGroups: groupOptions, ShowGroupSelectors } : ExportGroupListParams
) {
    const rowSorter = RowSorter(DefaultSortColumn),
        exportGroups = groupOptions
            .map(g => ExportGroup(BillingSystem, g))
            .sort(rowSorter.RowOrder);

    return {
        RowSorter: rowSorter,
        ExportGroups: exportGroups,
        ShowGroupSelectors
    };
}
export type ExportGroupList = ReturnType<typeof ExportGroupList>;

function ExportGroup(billingSystem : BillingSystem, data : ExportGroupOptions) {
    const defaultCurrency = () => billingSystem.Currency() || Currency.USD,
        invoiceTotal = TotalPriceForCharges(data.Charges, defaultCurrency, PRICE_PRECISION);

    return Object.assign(objectCreate(data), {
        InvoiceTotal: invoiceTotal
    });
}
export type ExportGroup = ReturnType<typeof ExportGroup>;


// WEBPACK FOOTER //
// ./apps/portcall/quickbooksexports/exportgrouplist.ts